import { useSyncExternalStore } from 'react';
import {
  subscribe,
  getRelays,
  getEventLog,
  getConnectedCount,
  getTotalCount,
  type RelayEntry,
  type EventLogEntry,
} from '@/lib/relayMonitor';

/**
 * Returns a live snapshot of the relay monitor state.
 * Uses useSyncExternalStore for concurrent-safe subscription.
 */
export function useRelayMonitor(): {
  relays: RelayEntry[];
  eventLog: EventLogEntry[];
  connectedCount: number;
  totalCount: number;
} {
  // Each selector is a separate subscription to avoid unnecessary object churn.
  const relays = useSyncExternalStore(subscribe, getRelays, getRelays);
  const eventLog = useSyncExternalStore(subscribe, getEventLog, getEventLog);
  const connectedCount = useSyncExternalStore(subscribe, getConnectedCount, getConnectedCount);
  const totalCount = useSyncExternalStore(subscribe, getTotalCount, getTotalCount);

  return { relays, eventLog, connectedCount, totalCount };
}

/**
 * Lightweight variant for components that only show the connection counts
 * (e.g. the always-mounted sidebar). Subscribes only to the numeric counts,
 * which compare by value — so the component re-renders only when a count
 * actually changes, not on every event-log/status snapshot rebuild.
 */
export function useRelayCounts(): { connectedCount: number; totalCount: number } {
  const connectedCount = useSyncExternalStore(subscribe, getConnectedCount, getConnectedCount);
  const totalCount = useSyncExternalStore(subscribe, getTotalCount, getTotalCount);
  return { connectedCount, totalCount };
}
